import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '../Constant/Auth';
import { getLibraries } from '../api/api';

/**
 * AdminLibraryContext
 *
 * The Super Admin (role_id === 1) has no single `library_id` of their own — they
 * oversee every library. Yet every data API is scoped to one library. This
 * provider gives the admin a single "currently selected library" that the whole
 * portal reads through `useActiveLibraryId()`, plus the full library list so a
 * selector can switch it.
 *
 * For every other role the provider is inert: `isAdmin` is false and nothing is
 * fetched, so `useActiveLibraryId()` falls back to the user's own `library_id`
 * and existing behaviour is unchanged.
 */
const AdminLibraryContext = createContext(null);

const STORAGE_KEY = 'admin_selected_library';

export const AdminLibraryProvider = ({ children }) => {
  const { user } = useAuth();
  const isAdmin = user?.role_id === 1; // Super Admin

  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLibraryId, setSelectedLibraryIdState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });

  const setSelectedLibraryId = (id) => {
    const next = id == null ? null : Number(id);
    setSelectedLibraryIdState(next);
    if (next == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(next));
  };

  useEffect(() => {
    let cancelled = false;

    if (!isAdmin) {
      setLibraries([]);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const data = await getLibraries();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setLibraries(list);

        // Default the selection to the first library if none is stored, or if
        // the stored one no longer exists.
        setSelectedLibraryIdState((prev) => {
          const exists = prev != null && list.some((l) => Number(l.id) === Number(prev));
          if (exists) return prev;
          const first = list[0]?.id != null ? Number(list[0].id) : null;
          if (first != null) localStorage.setItem(STORAGE_KEY, String(first));
          return first;
        });
      } catch (error) {
        console.error('Failed to load libraries for admin', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  return (
    <AdminLibraryContext.Provider
      value={{
        isAdmin,
        libraries,
        loading,
        selectedLibraryId,
        setSelectedLibraryId,
      }}
    >
      {children}
    </AdminLibraryContext.Provider>
  );
};

/** Returns the admin-library context, or null when rendered outside the provider. */
export const useAdminLibrary = () => useContext(AdminLibraryContext);

export default AdminLibraryContext;
